import { Head, Link, router, useForm } from '@inertiajs/react'
import { useState, useRef, useEffect, FormEvent } from 'react'
import {
  ArrowLeft,
  Globe,
  Users,
  MessageCircle,
  Hash,
  CheckCircle2,
  PauseCircle,
  ExternalLink,
  Calendar,
  Pencil,
  ToggleLeft,
  List,
  StickyNote,
} from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import { formatRelativeTime, formatDate, getInitials, getAvatarColor } from '@/lib/displayUtils'
import { stripHtml } from '@/lib/sanitizeHtml'
import type { Agent, Tag } from '@/types/shared'

interface CompanyDetail {
  id: number
  name: string
  domain: string | null
  remoteId: string | null
  metadata: Record<string, unknown> | null
  lastSeenAt: string | null
  createdAt: string
  usersCount: number
  conversationsCount: number
  companyNotes: CompanyNote[]
}

interface User {
  id: number
  name: string | null
  displayName: string
  email: string | null
  signedUpAt: string | null
  webSessionsCount: number
  lastSeenAt: string | null
  lastContactedAt: string | null
  lastHeardFromAt: string | null
  conversationsCount: number
}

interface CompanyNote {
  id: number
  body: string
  createdAt: string
  agent: Agent | null
}

interface EndUserSummary {
  id: number
  name: string | null
  displayName: string
  email: string | null
}

interface ConversationItem {
  id: number
  status: string
  priority: string
  lastMessage: string | null
  lastMessageAt: string | null
  messagesCount: number
  assignedAgent: Agent | null
  endUser: EndUserSummary
  tags: Tag[]
  createdAt: string
}

interface DataAttributeDef {
  id: number
  name: string
  label: string | null
  description: string | null
  dataType: string
  model: string
  listOptions: string[]
}

interface Props {
  company: CompanyDetail
  users: User[]
  conversations: ConversationItem[]
  currentAgent: Agent
  dataAttributes: DataAttributeDef[]
}

type TabType = 'users' | 'conversations'

export default function CompanyDetail({ company, users, conversations, currentAgent, dataAttributes }: Props) {
  const [activeTab, setActiveTab] = useState<TabType>('users')
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const editInputRef = useRef<HTMLInputElement | HTMLSelectElement>(null)
  const companyNotes = company.companyNotes || []
  const {
    data: noteData,
    setData: setNoteData,
    post: postNote,
    processing: noteProcessing,
    reset: resetNote,
    errors: noteErrors,
  } = useForm({ body: '' })

  useEffect(() => {
    if (editingKey && editInputRef.current) {
      editInputRef.current.focus()
    }
  }, [editingKey])

  const handleNoteSubmit = (event: FormEvent) => {
    event.preventDefault()
    postNote(`/contacts/companies/${company.id}/notes`, {
      preserveScroll: true,
      onSuccess: () => resetNote(),
    })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Aberta
          </span>
        )
      case 'closed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <CheckCircle2 className="w-3 h-3" />
            Fechada
          </span>
        )
      case 'snoozed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <PauseCircle className="w-3 h-3" />
            Adiada
          </span>
        )
      default:
        return null
    }
  }

  return (
    <>
      <Head title={`${company.name} - Detalhe da Empresa`} />

      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="contacts" />

        {/* Company Info Sidebar */}
        <aside className="w-[300px] shrink-0 bg-white border-r border-gray-200 overflow-y-auto">
          {/* Header with avatar and name */}
          <div className="p-4 border-b border-gray-200">
            <Link
              href="/contacts/companies"
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar às empresas
            </Link>

            <div className="flex items-center gap-3">
              <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center text-white text-lg font-medium ${getAvatarColor(
                  company.name
                )}`}
              >
                {getInitials(company.name)}
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{company.name}</h2>
                {company.domain && (
                  <a
                    href={`https://${company.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                  >
                    {company.domain}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Company Details */}
          <section className="p-4 border-b border-gray-200">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Informações
            </h3>
            <div className="space-y-2">
              {company.domain && (
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-gray-600 truncate">{company.domain}</span>
                </div>
              )}
              {company.remoteId && (
                <div className="flex items-center gap-2 text-sm">
                  <Hash className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-gray-600 truncate">{company.remoteId}</span>
                </div>
              )}
            </div>
          </section>

          {/* Stats */}
          <section className="p-4 border-b border-gray-200">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Estatísticas
            </h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-gray-50 rounded-lg p-2.5">
                <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                  <Users className="w-3.5 h-3.5" />
                  <span className="text-xs">Usuários</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">{company.usersCount}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5">
                <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span className="text-xs">Conversas</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">{company.conversationsCount}</p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Última atividade</span>
                <span className="text-gray-700">{formatRelativeTime(company.lastSeenAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Criada em</span>
                <span className="text-gray-700">{formatDate(company.createdAt)}</span>
              </div>
            </div>
          </section>

          {/* Company Notes */}
          <section className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                <StickyNote className="w-3.5 h-3.5" />
                Notas
              </h3>
              <span className="text-xs text-gray-400">{companyNotes.length}</span>
            </div>
            <form onSubmit={handleNoteSubmit} className="space-y-2">
              <textarea
                className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="Adicionar nota interna..."
                value={noteData.body}
                onChange={(e) => setNoteData('body', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault()
                    if (noteData.body.trim()) e.currentTarget.form?.requestSubmit()
                  }
                }}
              />
              {noteErrors.body && (
                <p className="text-xs text-red-600">{noteErrors.body}</p>
              )}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={noteProcessing || !noteData.body.trim()}
                  className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Salvar nota
                </button>
              </div>
            </form>
            <div className="mt-3 space-y-2">
              {companyNotes.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhuma nota ainda</p>
              ) : (
                companyNotes.map((note) => (
                  <div key={note.id} className="rounded-lg border border-gray-200 bg-gray-50 p-2.5">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.body}</p>
                    <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                      <span>{note.agent?.name || 'Sistema'}</span>
                      <span>{formatRelativeTime(note.createdAt)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Metadata / Custom Attributes */}
          {company.metadata && Object.keys(company.metadata).length > 0 && (
            <section className="p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Dados Personalizados
              </h3>
              <div className="space-y-1.5">
                {Object.entries(company.metadata).map(([key, value]) => {
                  const attrDef = dataAttributes?.find(da => da.name === key)
                  const displayLabel = attrDef?.label || key
                  const dataType = attrDef?.dataType || 'string'

                  const formatValue = (val: unknown): string => {
                    if (val === null || val === undefined) return '—'
                    switch (dataType) {
                      case 'boolean': return val ? 'Sim' : 'Não'
                      case 'date':
                      case 'datetime': return val ? formatRelativeTime(String(val)) : '—'
                      case 'integer': return Number(val).toLocaleString('pt-BR')
                      case 'float': return Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                      default: return String(val)
                    }
                  }

                  const startEditing = () => {
                    setEditingKey(key)
                    if (dataType === 'boolean') {
                      setEditValue(value ? 'true' : 'false')
                    } else {
                      setEditValue(value !== null && value !== undefined ? String(value) : '')
                    }
                  }

                  const saveEdit = () => {
                    router.patch(`/contacts/companies/${company.id}/metadata`, {
                      key, value: editValue, data_type: dataType,
                    }, { preserveScroll: true, preserveState: true })
                    setEditingKey(null)
                  }

                  const cancelEdit = () => setEditingKey(null)

                  const TypeIndicator = () => {
                    switch (dataType) {
                      case 'boolean': return <ToggleLeft className="w-3 h-3 text-gray-400" />
                      case 'date':
                      case 'datetime': return <Calendar className="w-3 h-3 text-gray-400" />
                      case 'list': return <List className="w-3 h-3 text-gray-400" />
                      case 'integer':
                      case 'float': return <span className="text-[10px] font-mono text-gray-400">#</span>
                      default: return null
                    }
                  }

                  return (
                    <div key={key} className="group flex items-start justify-between text-sm py-1 rounded hover:bg-gray-50 px-1 -mx-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <TypeIndicator />
                        <span className="text-gray-500 truncate" title={key}>{displayLabel}</span>
                      </div>
                      {editingKey === key ? (
                        <div className="flex items-center gap-1">
                          {dataType === 'boolean' ? (
                            <select
                              ref={editInputRef as React.RefObject<HTMLSelectElement>}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit() }}
                              className="px-1.5 py-0.5 border border-blue-300 rounded text-xs focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                            >
                              <option value="true">Sim</option>
                              <option value="false">Não</option>
                            </select>
                          ) : dataType === 'list' && attrDef?.listOptions?.length ? (
                            <select
                              ref={editInputRef as React.RefObject<HTMLSelectElement>}
                              value={editValue}
                              onChange={(e) => { setEditValue(e.target.value) }}
                              onBlur={saveEdit}
                              onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit() }}
                              className="px-1.5 py-0.5 border border-blue-300 rounded text-xs focus:ring-1 focus:ring-blue-500 outline-none bg-white max-w-[140px]"
                            >
                              <option value="">—</option>
                              {attrDef.listOptions.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              ref={editInputRef as React.RefObject<HTMLInputElement>}
                              type={dataType === 'integer' || dataType === 'float' ? 'number' : dataType === 'date' || dataType === 'datetime' ? 'date' : 'text'}
                              step={dataType === 'float' ? '0.01' : dataType === 'integer' ? '1' : undefined}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit()
                                if (e.key === 'Escape') cancelEdit()
                              }}
                              className="px-1.5 py-0.5 border border-blue-300 rounded text-xs w-[120px] focus:ring-1 focus:ring-blue-500 outline-none"
                            />
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-700 truncate max-w-[140px]" title={String(value)}>
                            {formatValue(value)}
                          </span>
                          <button
                            onClick={startEditing}
                            className="p-0.5 text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          )}
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 bg-white">
          {/* Tabs Header */}
          <div className="h-14 border-b border-gray-200 flex items-center px-4 gap-2">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                activeTab === 'users'
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Usuários ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('conversations')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                activeTab === 'conversations'
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Conversas ({conversations.length})
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'users' && (
              <div className="p-4 space-y-3">
                {users.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => router.visit(`/contacts/${user.id}`)}
                    className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
                  >
                    {/* Header with avatar and name */}
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-medium ${getAvatarColor(
                          user.name
                        )}`}
                      >
                        {getInitials(user.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold text-gray-900 truncate">
                          {user.displayName}
                        </p>
                        {user.email && (
                          <p className="text-sm text-gray-500 truncate">{user.email}</p>
                        )}
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="grid grid-cols-5 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500 mb-1">Cadastro</p>
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>{formatRelativeTime(user.signedUpAt)}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Sessões</p>
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>{user.webSessionsCount}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Última visita</p>
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>{formatRelativeTime(user.lastSeenAt)}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Último contato</p>
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>{formatRelativeTime(user.lastContactedAt)}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Última resposta</p>
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>{formatRelativeTime(user.lastHeardFromAt)}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}

                {users.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Nenhum usuário nesta empresa</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'conversations' && (
              <div className="divide-y divide-gray-100">
                {conversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => router.visit(`/inbox/${conv.id}`)}
                    className="w-full text-left p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(conv.status)}
                        <span className="text-sm text-gray-600">{conv.endUser.displayName}</span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {formatRelativeTime(conv.lastMessageAt)}
                      </span>
                    </div>
                    {conv.lastMessage && (
                      <p className="text-sm text-gray-600 truncate mt-1">{stripHtml(conv.lastMessage)}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span>{conv.messagesCount} mensagens</span>
                      {conv.assignedAgent && <span>Atribuído a {conv.assignedAgent.name}</span>}
                    </div>
                  </button>
                ))}

                {conversations.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Nenhuma conversa nesta empresa</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
